import { useCallback, useEffect, useLayoutEffect, useRef, type MutableRefObject } from 'react';
import type {
    SmriqLastCompletedServiceSelection,
    SmriqOperationExclusion,
    SmriqPlanSettings,
} from '../types/smriq.types';
import { smriqExclusionsKey } from '../utils/smriqExclusionsKey';
import type { GeneratePlanInput } from './useSmriqJourney';
import type { useSmriqLastCompletedService } from './useSmriqLastCompletedService';

type LastCompletedServiceGate = Pick<
    ReturnType<typeof useSmriqLastCompletedService>,
    'selection' | 'generateAllowed' | 'canGeneratePlan' | 'changeSelection'
>;

interface UseSmriqDeclineRegenerateArgs {
    settingsRef: MutableRefObject<SmriqPlanSettings | null | undefined>;
    exclusions: SmriqOperationExclusion[];
    exclusionsSupported: boolean;
    lastCompleted: LastCompletedServiceGate;
    isGenerating: boolean;
    needsReconfirm: boolean;
    sessionExpired: boolean;
    planFieldsEdited: boolean;
    generatePlan: (input: GeneratePlanInput) => void;
    toggleDecline: (visitId: string, itemId: string) => void;
    markStale: () => void;
    debounceMs: number;
}

/** Debounces include/decline toggles into one regenerate and remembers which decline set the
 *  plan on screen was priced against. A selection edit or plan-controls draft holds it until resumed. */
export function useSmriqDeclineRegenerate({
    settingsRef,
    exclusions,
    exclusionsSupported,
    lastCompleted,
    isGenerating,
    needsReconfirm,
    sessionExpired,
    planFieldsEdited,
    generatePlan,
    toggleDecline,
    markStale,
    debounceMs,
}: UseSmriqDeclineRegenerateArgs) {
    const { selection, generateAllowed, canGeneratePlan, changeSelection } = lastCompleted;
    const disableToggles = isGenerating || sessionExpired || needsReconfirm || !canGeneratePlan;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastAppliedKeyRef = useRef<string | null>(null);
    const interruptedRef = useRef(false);
    const selectionRef = useRef(selection);
    const planFieldsEditedRef = useRef(planFieldsEdited);
    // Mirrored in a layout effect so only committed values reach a timer, before any timer can fire.
    useLayoutEffect(() => {
        selectionRef.current = selection;
        planFieldsEditedRef.current = planFieldsEdited;
    }, [selection, planFieldsEdited]);

    const cancelPending = useCallback(() => {
        interruptedRef.current = false;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const markApplied = useCallback(() => {
        lastAppliedKeyRef.current = smriqExclusionsKey(exclusions);
    }, [exclusions]);

    // Kept current every render so the debounced fire reads the latest exclusions and guards,
    // never a value closed over when the timer was scheduled.
    const fireRef = useRef<() => void>(() => {});
    useEffect(() => {
        fireRef.current = () => {
            if (planFieldsEditedRef.current) {
                interruptedRef.current = true;
                return;
            }
            const settings = settingsRef.current;
            const blocked = isGenerating || needsReconfirm || sessionExpired || !generateAllowed;
            if (!settings || blocked || !exclusionsSupported) return;
            const key = smriqExclusionsKey(exclusions);
            if (key === lastAppliedKeyRef.current) return;
            lastAppliedKeyRef.current = key;
            generatePlan({
                ...settings,
                recommended_operation_exclusions: exclusions,
                last_completed_service: selectionRef.current,
            });
        };
    });

    useEffect(() => cancelPending, [cancelPending]);

    const schedule = useCallback(() => {
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            fireRef.current();
        }, debounceMs);
    }, [debounceMs]);

    const handleToggleDecline = useCallback(
        (visitId: string, itemId: string) => {
            if (disableToggles) return;
            toggleDecline(visitId, itemId);
            // No settings on the quote yet: mark it stale so the open manual controls prompt an
            // Update rather than guess a duration.
            if (!settingsRef.current) {
                markStale();
                return;
            }
            cancelPending();
            if (planFieldsEdited) {
                interruptedRef.current = true;
                return;
            }
            schedule();
        },
        [disableToggles, planFieldsEdited, toggleDecline, markStale, cancelPending, settingsRef, schedule],
    );

    const handleSelectionChange = useCallback(
        (next: SmriqLastCompletedServiceSelection | null, valid: boolean) => {
            const interrupted = interruptedRef.current || timerRef.current !== null;
            cancelPending();
            interruptedRef.current = interrupted;
            changeSelection(next, valid);
        },
        [cancelPending, changeSelection],
    );

    // Called when an edit is cancelled, so toggles held back by a selection edit or plan-controls draft still re-price.
    const resumeInterrupted = useCallback(() => {
        if (!interruptedRef.current) return;
        interruptedRef.current = false;
        schedule();
    }, [schedule]);

    return {
        handleToggleDecline,
        handleSelectionChange,
        cancelPending,
        resumeInterrupted,
        markApplied,
        disableToggles,
    };
}
